/**
 * PoliciesListPage — `/admin/policies`
 *
 * Lists every business policy with its scope, status and priority.
 * Operators can filter by status and by action-prefix (e.g. `booking.*`).
 */
import { CaretRight, Funnel, Warning } from '@phosphor-icons/react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Card } from '../../shared/ui/Card';
import { Input } from '../../shared/ui/Input';
import { Label } from '../../shared/ui/Label';
import { Select } from '../../shared/ui/Select';
import { StatusBadge, type StatusVariant } from '../../shared/ui/StatusBadge';
import { cn } from '../../shared/utils/cn';
import { useBusinessPolicies } from '../../features/business-policies/useBusinessPolicies';
import {
  POLICY_STATUSES,
  type PolicyStatus,
} from '../../features/business-policies/types';

const STATUS_VARIANT: Record<PolicyStatus, StatusVariant> = {
  draft: 'neutral',
  shadow: 'info',
  active: 'success',
  deprecated: 'warning',
  archived: 'neutral',
};

export function PoliciesListPage(): JSX.Element {
  const { t } = useTranslation('adminPolicies');
  const policiesQuery = useBusinessPolicies();
  const [statusFilter, setStatusFilter] = useState<'all' | PolicyStatus>('all');
  const [actionQuery, setActionQuery] = useState('');

  const filtered = useMemo(() => {
    if (!policiesQuery.data) return [];
    return policiesQuery.data.filter((p) => {
      if (statusFilter !== 'all' && p.status !== statusFilter) return false;
      if (actionQuery.trim()) {
        const q = actionQuery.trim().toLowerCase();
        const hit = p.scopeActions.some((a) => a.toLowerCase().includes(q));
        const nameHit = p.name.toLowerCase().includes(q);
        const idHit = p.policyId.toLowerCase().includes(q);
        if (!hit && !nameHit && !idHit) return false;
      }
      return true;
    });
  }, [policiesQuery.data, statusFilter, actionQuery]);

  return (
    <div className="px-biz-card-lg py-biz-section max-w-[1200px] mx-auto">
      <header className="mb-biz-section">
        <h1 className="text-biz-3xl biz-heading font-semibold mb-biz-content">
          {t('list.title')}
        </h1>
        <p className="text-biz-base text-biz-muted-foreground">
          {t('list.subtitle')}
        </p>
      </header>

      <Card className="mb-biz-section">
        <div className="px-biz-card py-biz-content-lg grid grid-cols-1 md:grid-cols-[220px_1fr] gap-biz-content-lg items-end">
          <div>
            <Label htmlFor="policy-status">{t('list.statusFilter')}</Label>
            <Select
              id="policy-status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
            >
              <option value="all">{t('list.anyStatus')}</option>
              {POLICY_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {t(`status.${s}` as const)}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <Label htmlFor="policy-search">{t('list.searchLabel')}</Label>
            <div className="relative">
              <Input
                id="policy-search"
                type="search"
                value={actionQuery}
                onChange={(e) => setActionQuery(e.target.value)}
                placeholder={t('list.searchPlaceholder')}
              />
              <Funnel
                size={16}
                weight="regular"
                aria-hidden="true"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-biz-muted-foreground pointer-events-none"
              />
            </div>
          </div>
        </div>
      </Card>

      {policiesQuery.isLoading ? (
        <LoadingSkeleton />
      ) : policiesQuery.isError ? (
        <div
          role="alert"
          className="flex items-start gap-biz-content-lg px-biz-content-lg py-biz-content-lg rounded-biz-card bg-biz-danger-subtle text-biz-danger border border-biz-danger/30"
        >
          <Warning weight="regular" size={20} aria-hidden="true" className="shrink-0" />
          <p className="text-biz-sm">{t('list.loadError')}</p>
        </div>
      ) : filtered.length === 0 ? (
        <Card className="px-biz-card-lg py-biz-section text-center text-biz-muted-foreground">
          {t('list.empty')}
        </Card>
      ) : (
        <Card className="divide-y divide-biz-border">
          {filtered.map((p) => (
            <Link
              key={p.id}
              to={`/admin/policies/${encodeURIComponent(p.policyId)}`}
              className={cn(
                'grid gap-biz-content-lg',
                'grid-cols-1 md:grid-cols-[1fr_auto_auto_auto]',
                'px-biz-card py-biz-content-lg min-h-[96px]',
                'hover:bg-biz-muted',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring focus-visible:ring-inset',
                'transition-colors duration-biz-fast',
              )}
              aria-label={`${p.policyId} ${p.name}`}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-biz-content flex-wrap">
                  <span className="font-mono text-biz-xs text-biz-muted-foreground">
                    {p.policyId}
                  </span>
                  <h2 className="text-biz-base font-semibold truncate">{p.name}</h2>
                  <span className="font-mono text-biz-xs text-biz-muted-foreground">
                    v{p.version}
                  </span>
                </div>
                {p.description ? (
                  <p className="mt-1 text-biz-sm text-biz-muted-foreground line-clamp-2">
                    {p.description}
                  </p>
                ) : null}
                <div className="mt-biz-content flex items-center gap-biz-content flex-wrap">
                  {p.scopeActions.slice(0, 4).map((a) => (
                    <span
                      key={a}
                      className="inline-flex items-center px-biz-content py-0.5 rounded-biz-full border border-biz-border bg-biz-muted font-mono text-biz-xs"
                    >
                      {a}
                    </span>
                  ))}
                  {p.scopeActions.length > 4 ? (
                    <span className="text-biz-xs text-biz-muted-foreground">
                      +{p.scopeActions.length - 4}
                    </span>
                  ) : null}
                </div>
              </div>

              <div className="md:text-right text-biz-xs text-biz-muted-foreground self-center biz-numeric">
                {t('list.priorityLabel')} {p.priority}
              </div>

              <StatusBadge
                variant={STATUS_VARIANT[p.status]}
                label={t(`status.${p.status}` as const)}
                className="self-center"
              />

              <CaretRight
                size={20}
                weight="regular"
                aria-hidden="true"
                className="shrink-0 text-biz-muted-foreground self-center hidden md:block"
              />
            </Link>
          ))}
        </Card>
      )}
    </div>
  );
}

function LoadingSkeleton(): JSX.Element {
  return (
    <Card className="divide-y divide-biz-border" aria-busy="true" aria-live="polite">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="px-biz-card py-biz-content-lg">
          <div className="h-6 w-1/3 bg-biz-muted rounded animate-pulse mb-2" />
          <div className="h-4 w-2/3 bg-biz-muted rounded animate-pulse" />
        </div>
      ))}
    </Card>
  );
}
